import { useCallback, useState } from 'react';
import { pexelsApiService, PexelsPhoto } from './services/api';

export function usePexelsCore() {
  const [photos, setPhotos] = useState<PexelsPhoto[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [currentQuery, setCurrentQuery] = useState('');

  const searchPhotos = useCallback(async (query: string, reset: boolean = true) => {
    try {
      setLoading(true);
      setError(null);
      
      if (reset) {
        setPhotos([]);
        setCurrentPage(1);
        setCurrentQuery(query);
      }

      const response = await pexelsApiService.searchPhotos(
        query, 
        reset ? 1 : currentPage + 1, 
        20
      );

      if (reset) {
        setPhotos(response.photos);
        setCurrentPage(1);
      } else {
        setPhotos(prev => [...prev, ...response.photos]);
        setCurrentPage(prev => prev + 1);
      }

      setHasMore(!!response.next_page);
    } catch (err: any) {
      setError(err.message || 'Error al buscar imágenes');
    } finally {
      setLoading(false);
    }
  }, [currentPage]);

  const loadMorePhotos = useCallback(() => {
    if (!loading && hasMore && currentQuery) {
      searchPhotos(currentQuery, false);
    }
  }, [loading, hasMore, currentQuery, searchPhotos]);

  const loadCuratedPhotos = useCallback(async (reset: boolean = true) => {
    try {
      setLoading(true);
      setError(null);
      
      if (reset) {
        setPhotos([]);
        setCurrentPage(1);
        setCurrentQuery('');
      }

      const response = await pexelsApiService.getPopularPhotos(
        reset ? 1 : currentPage + 1, 
        20
      );

      if (reset) {
        setPhotos(response.photos);
        setCurrentPage(1);
      } else {
        setPhotos(prev => [...prev, ...response.photos]);
        setCurrentPage(prev => prev + 1);
      }

      setHasMore(!!response.next_page);
    } catch (err: any) {
      setError(err.message || 'Error al cargar imágenes');
    } finally {
      setLoading(false);
    }
  }, [currentPage]);

  const downloadPhoto = useCallback(async (photo: PexelsPhoto): Promise<Blob> => {
    try {
      return await pexelsApiService.downloadPhoto(photo);
    } catch (err: any) {
      throw new Error('Error al descargar la imagen');
    }
  }, []);

  const clearPhotos = useCallback(() => {
    setPhotos([]);
    setCurrentPage(1);
    setCurrentQuery('');
    setHasMore(true);
    setError(null);
  }, []);

  return {
    photos,
    loading,
    error,
    hasMore,
    searchPhotos,
    loadMorePhotos,
    loadCuratedPhotos,
    downloadPhoto,
    clearPhotos
  };
}
